import { Controller, useFormContext } from 'react-hook-form';

import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import type { AutocompleteProps } from '@mui/material/Autocomplete';

export type AutocompleteSelectBaseProps = Omit<
  AutocompleteProps<any, boolean, boolean, boolean>,
  'renderInput'
>;

export type RHFAutocompleteProps = AutocompleteSelectBaseProps & {
  name: string;
  label?: string;
  placeholder?: string;
  helperText?: React.ReactNode;
  defaultValue?: any;
  options: { id: string; name: string; disabled?: boolean }[];
  onValueChange?: (value: string | undefined) => void;
};
export function RHFAutocompleteSelect({
  name,
  label,
  helperText,
  placeholder,
  defaultValue,
  options,
  onValueChange,
  ...other
}: RHFAutocompleteProps) {
  const { control, setValue } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue || null}
      render={({ field, fieldState: { error } }) => {
        const selectedOption = options.find((option) => option.id === field.value);

        return (
          <Autocomplete
            {...field}
            value={selectedOption || null}
            options={options}
            getOptionKey={(option) => option.id}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_, newValue) => {
              setValue(name, newValue ? newValue.id : '', {
                shouldValidate: true,
                shouldDirty: true,
              });
              if (onValueChange) {
                onValueChange(newValue ? newValue.id : undefined);
              }
            }}
            renderOption={(props, option) => {
              const { key, ...otherProps } = props;
              return (
                <li
                  key={key}
                  {...otherProps}
                  style={{
                    opacity: option.disabled ? 0.5 : 1,
                    pointerEvents: option.disabled ? 'none' : 'auto',
                  }}
                >
                  {option.name}
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                placeholder={placeholder}
                error={!!error}
                helperText={error ? error?.message : helperText}
                inputProps={{ ...params.inputProps, autoComplete: '' }}
              />
            )}
            {...other}
          />
        );
      }}
    />
  );
}
